import React, { Fragment, useContext, useEffect, useState } from 'react'
import {
  Button,
  Modal,
  Typography,
  Paper,
  Box,
  CircularProgress,
} from '@mui/material'
import { makeStyles } from "@mui/styles";
import { useHistory } from 'react-router-dom'
import ENVIRONMENT from '../environment/environment'
import iso from '../assets/isotipo.svg'
import sessionApi from '../services/session'
import { UserContext } from '../context/userContext'
import { getAuctionSocket } from '../services/socketAuction'
import { USER_STATUS } from '../utils/constants'
import localStorageService from '../services/localStorage'

const useStyles = makeStyles((theme) => ({
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  content: {
    maxWidth: 350,
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
    paddingLeft: theme.spacing(6),
    paddingRight: theme.spacing(6),
    borderWidth: theme.spacing(0.6),
    borderRadius: 75,
    borderStyle: 'solid',
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.primary.lightBackground,
  },
  continueButton: {
    fontFamily: 'ITC Officina Sans Std',
    fontWeight: 'bold',
    fontSize: 18,
    backgroundColor: theme.palette.primary.mainAuctionButton,
    color: theme.palette.primary.lightBackground,
    minWidth: 100,
  },
  text: {
    fontFamily: 'ITC Officina Sans Std, Book',
    color: theme.palette.primary.main,
    fontSize: 16,
    textAlign: 'center',
  },
  circle: {
    borderWidth: theme.spacing(0.6),
    borderRadius: 200,
    borderStyle: 'solid',
    borderColor: theme.palette.primary.main,
    width: 65,
    height: 65,
    marginBottom: theme.spacing(2),
  },
  '@-webkit-keyframes rotateY': {
    '0%': {
      transform: 'rotateY(0deg)',
    },
    '100%': {
      transform: 'rotateY(360deg)',
    },
  },
  '@keyframes rotateY': {
    '0%': {
      transform: 'rotateY(0deg)',
    },
    '100%': {
      transform: 'rotateY(360deg)',
    },
  },
  iso: {
    padding: theme.spacing(1),
    backgroundColor: 'transparent',
    width: 50,
    height: 50,
    //'-webkit-animation': '$rotateY 1s infinite linear',
    animation: '$rotateY 5s infinite ease-in-out',
  },
}))

export default function AuthGuard({ children, GuardComponent }) {
  const classes = useStyles()
  const history = useHistory()
  const Guard = GuardComponent || Fragment
  const { userData, setUserData } = useContext(UserContext)
  const [open, setOpen] = useState(false)
  const connectionStatus = userData?.connectionStatus
  let message = ''

  switch (connectionStatus) {
    case USER_STATUS.INVALID_SESSION:
      message = 'SESION EXPIRADA'
      break
    case USER_STATUS.LOST_CONNECTION:
      message =
        'SE HA PERDIDO LA CONEXIÓN CON EL SERVIDOR, ESTAMOS INTENTANDO CONECTARLO AL REMATE.'
      break
    case USER_STATUS.DUPLICATED_SESSION:
      message = 'HA INGRESADO OTRO USUARIO CON LA MISMA SESIÓN A ESTE REMATE.'
      break
    case USER_STATUS.EXPULSED:
      message = 'HA SIDO EXPULSADO DE ESTE REMATE.'
      break
    default:
      break
  }

  const handleClose = (reason) => { }
  const onClickContinue = () => {
    try {
      const socket = getAuctionSocket()
      socket.disconnect()
    } catch (error) {
      console.error('Error al desconectar el socket', error)
    }
    try {
      localStorageService.clear()
      setUserData()
    } catch (error) {
      console.error('Error al limpiar la sesión', error)
    }
    window.location.href = ENVIRONMENT.WEB.LOGIN_URL
  }
  useEffect(() => {
    //
    const checkSession = async () => {
      try {
        const result = await sessionApi.me() // just to confirm that the user is logged, if not, it will throw an error
        setUserData((user) => ({
          ...user,
          ...result.data,
          isGuest: false,
          roleId: result.data.roleId ? result.data.roleId : 5,
        }))
      } catch (error) {
        console.error({ error })
        setUserData((u) => ({
          ...u,
          connectionStatus: USER_STATUS.INVALID_SESSION,
        }))
        setOpen(true)
      }
    }
    if (!userData?.isGuest && userData?.token) {
      checkSession()
    } else if (userData?.isGuest && !userData?.token) {
      setUserData((u) => ({
        ...u,
        connectionStatus: USER_STATUS.INVALID_SESSION,
      }))
      setOpen(true)
    }
    const relojCheckeo = setInterval(() => {
      if (!userData?.isGuest && userData?.token) {
        checkSession()
      } else if (!userData?.token) {
        // Ni invitado con token propio ni usuario registrado con token: no hay
        // sesión válida (localStorage vacío/corrupto, o llegó a una ruta
        // protegida sin haber iniciado sesión), así que se marca como inválida
        // en vez de dejar pasar silenciosamente.
        setUserData((u) => ({
          ...u,
          connectionStatus: USER_STATUS.INVALID_SESSION,
        }))
        setOpen(true)
      }
    }, 1800000) // esto es en milisegundos 1800*1000

    // saneo
    return () => {
      clearInterval(relojCheckeo)
    }
  }, [setUserData, userData?.isGuest, userData?.token])


  useEffect(() => {
    if (userData) {
      if (userData.connectionStatus === USER_STATUS.INVALID_SESSION) {
        setOpen(true)
      } else if (userData.connectionStatus === USER_STATUS.LOST_CONNECTION) {
        setOpen(true)
      } else if (userData.connectionStatus === USER_STATUS.DUPLICATED_SESSION) {
        setOpen(true)
      } else if (userData.connectionStatus === USER_STATUS.EXPULSED) {
        setOpen(true)
      } else if (!userData.connectionStatus) {
        setOpen(false)
      }
    }
  }, [userData, history])


  return (
    <>
      <Modal
        className={classes.modal}
        open={open}
        onClose={handleClose}
        disableEscapeKeyDown
        aria-labelledby="expired-session-modal"
        aria-describedby="expired-session-modal"
      >
        <Paper className={classes.content}>
          <Box className={classes.circle}>
            <img alt="logo adornment" src={iso} className={classes.iso} />
          </Box>
          <Box mb={2} display="flex" flexDirection="column" alignItems="center">
            {connectionStatus === USER_STATUS.INVALID_SESSION ? (
              <>
                <Typography className={classes.text}>{message}</Typography>
                <Typography className={classes.text}>
                  INICIA SESIÓN NUEVAMENTE
                </Typography>
              </>
            ) : null}
            {connectionStatus === USER_STATUS.LOST_CONNECTION ? (
              <>
                <Typography className={classes.text}>{message}</Typography>
                <CircularProgress />
                <Typography className={classes.text}>ó</Typography>
                <Typography className={classes.text}>
                  INICIA SESIÓN NUEVAMENTE
                </Typography>
              </>
            ) : null}
            {connectionStatus === USER_STATUS.DUPLICATED_SESSION ? (
              <>
                <Typography className={classes.text}>{message}</Typography>
                <Typography className={classes.text}>
                  INICIA SESIÓN NUEVAMENTE
                </Typography>
              </>
            ) : null}
            {connectionStatus === USER_STATUS.EXPULSED ? (
              <>
                <Typography className={classes.text}>{message}</Typography>
              </>
            ) : null}
          </Box>
          <Button className={classes.continueButton} onClick={onClickContinue}>
            Continuar
          </Button>
        </Paper>
      </Modal>
      <Guard>{children}</Guard>
    </>
  )
}
